export const MIME_TYPE_EXTENSION_MAP = {
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/pjpeg": "jpg",
  "image/png": "png",
  "image/x-png": "png",
  "image/webp": "webp",
  "image/heic": "heic",
  "image/heif": "heif",
  "image/heic-sequence": "heic",
  "image/heif-sequence": "heif",
  "video/mp4": "mp4",
  "video/quicktime": "mov",
  "video/webm": "webm",
  "video/m4v": "m4v",
};

export const DEFAULT_IMAGE_UPLOAD_NAME = "catch-photo.jpg";
export const DEFAULT_VIDEO_UPLOAD_NAME = "catch-video.mp4";
export const SAFE_DIRECT_IMAGE_MIME_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/pjpeg",
  "image/png",
  "image/x-png",
  "image/webp",
]);
export const SAFE_DIRECT_IMAGE_EXTENSIONS = new Set([
  "jpg",
  "jpeg",
  "jpe",
  "jfif",
  "png",
  "webp",
]);

export const CATCH_IMAGE_NORMALIZATION_ERROR_CODE =
  "catch_image_normalization_failed";
export const CATCH_IMAGE_PREVIEW_ERROR_CODE = "catch_image_preview_failed";
export const CATCH_IMAGE_NORMALIZATION_ERROR_MESSAGE =
  "catch_image_normalization_failed_message";
export const CATCH_IMAGE_PREVIEW_ERROR_MESSAGE =
  "catch_image_preview_failed_message";
export const CATCH_IMAGE_PREVIEW_UNAVAILABLE_MESSAGE =
  "catch_image_preview_unavailable_message";
export const CATCH_MULTIPART_BUILD_ERROR_CODE =
  "catch_multipart_build_failed";
export const CATCH_MULTIPART_BUILD_ERROR_MESSAGE =
  "catch_upload_prepare_failed_message";
export const CATCH_ANDROID_PICKER_UNREADABLE_ERROR_CODE =
  "catch_android_picker_unreadable";
export const CATCH_ANDROID_PICKER_UNREADABLE_ERROR_MESSAGE =
  "catch_android_picker_unreadable_message";
export const CATCH_IMAGE_HEIC_UNSUPPORTED_ERROR_CODE =
  "catch_image_heic_unsupported";
export const CATCH_IMAGE_HEIC_UNSUPPORTED_ERROR_MESSAGE =
  "catch_image_heic_unsupported_message";

const CATCH_MEDIA_MESSAGE_KEYS = new Set([
  CATCH_IMAGE_NORMALIZATION_ERROR_MESSAGE,
  CATCH_IMAGE_PREVIEW_ERROR_MESSAGE,
  CATCH_IMAGE_PREVIEW_UNAVAILABLE_MESSAGE,
  CATCH_MULTIPART_BUILD_ERROR_MESSAGE,
  CATCH_ANDROID_PICKER_UNREADABLE_ERROR_MESSAGE,
  CATCH_IMAGE_HEIC_UNSUPPORTED_ERROR_MESSAGE,
]);

export const resolveCatchMediaMessage = (message, t) => {
  if (typeof message !== "string" || !message) {
    return "";
  }

  if (CATCH_MEDIA_MESSAGE_KEYS.has(message) && typeof t === "function") {
    return t(message);
  }

  return message;
};

export const normalizeMimeType = (value) =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

export const buildCatchMediaClientError = (message, code, cause) => {
  const error = new Error(message);
  error.code = code;
  if (cause) {
    error.cause = cause;
  }
  return error;
};

export const isCatchMediaClientError = (error, code) =>
  Boolean(
    error &&
      typeof error === "object" &&
      (code ? error.code === code : typeof error.code === "string")
  );

export const getFilenameExtension = (filename) => {
  const normalizedFilename =
    typeof filename === "string" ? filename.trim() : "";

  if (!normalizedFilename.includes(".")) {
    return "";
  }

  return normalizedFilename.split(".").pop().trim().toLowerCase();
};

export const replaceFilenameExtension = (filename, nextExtension) => {
  const normalizedFilename =
    typeof filename === "string" ? filename.trim() : "";
  const normalizedExtension =
    typeof nextExtension === "string" ? nextExtension.trim().toLowerCase() : "";

  if (!normalizedExtension) {
    return normalizedFilename || DEFAULT_IMAGE_UPLOAD_NAME;
  }

  if (!normalizedFilename) {
    return `catch-upload.${normalizedExtension}`;
  }

  const lastDotIndex = normalizedFilename.lastIndexOf(".");
  if (lastDotIndex <= 0) {
    return `${normalizedFilename}.${normalizedExtension}`;
  }

  return `${normalizedFilename.slice(0, lastDotIndex)}.${normalizedExtension}`;
};

export const inferFilenameFromMimeType = (mimeType, mediaType) => {
  const normalizedMimeType = normalizeMimeType(mimeType);
  const extension = MIME_TYPE_EXTENSION_MAP[normalizedMimeType];

  if (extension) {
    return `catch-upload.${extension}`;
  }

  return mediaType === "video"
    ? DEFAULT_VIDEO_UPLOAD_NAME
    : DEFAULT_IMAGE_UPLOAD_NAME;
};

export const inferSelectedMediaType = (file, fallbackType = "image") => {
  const normalizedMimeType =
    typeof file?.type === "string" ? file.type.toLowerCase() : "";

  if (normalizedMimeType.startsWith("video/")) {
    return "video";
  }

  if (normalizedMimeType.startsWith("image/")) {
    return "image";
  }

  return fallbackType;
};

export const isDirectCatchImageFormatSafe = (file, fallbackName = "") => {
  const normalizedMimeType = normalizeMimeType(file?.type);
  const candidateFilename =
    (typeof fallbackName === "string" && fallbackName.trim()) ||
    (typeof file?.name === "string" && file.name.trim()) ||
    "";
  const extension = getFilenameExtension(candidateFilename);

  return (
    SAFE_DIRECT_IMAGE_MIME_TYPES.has(normalizedMimeType) ||
    SAFE_DIRECT_IMAGE_EXTENSIONS.has(extension)
  );
};

export const requiresClientImagePreparation = (file, fallbackName = "") =>
  !isDirectCatchImageFormatSafe(file, fallbackName);

export const buildUploadFilename = (preview, mediaType, mimeType) => {
  const candidateName =
    (typeof preview?.name === "string" && preview.name.trim()) ||
    (typeof preview?.file?.name === "string" && preview.file.name.trim()) ||
    "";

  if (candidateName) {
    return candidateName;
  }

  return inferFilenameFromMimeType(mimeType, mediaType);
};

export const buildRejectedImageSelectionState = (
  selectionId,
  nonBlockingMessage = CATCH_ANDROID_PICKER_UNREADABLE_ERROR_MESSAGE,
  snapshotStatus = "failed"
) => ({
  selectionId,
  selectedFile: null,
  originalFile: null,
  snapshotFile: null,
  snapshotStatus,
  uploadFile: null,
  uploadFilename: "",
  uploadMimeType: "",
  mediaType: null,
  previewUrl: "",
  previewSource: "",
  previewStatus: "failed",
  exifStatus: "failed",
  submitReady: false,
  blockingError: "",
  blockingErrorCode: "",
  nonBlockingMessage,
});

export const normalizeBlobMimeType = (blob, mimeType) => {
  if (
    !mimeType ||
    typeof Blob === "undefined" ||
    !(blob instanceof Blob) ||
    normalizeMimeType(blob.type) === mimeType
  ) {
    return blob;
  }

  try {
    return blob.slice(0, blob.size, mimeType);
  } catch {
    return blob;
  }
};
